/**
 * React Query hook for conditional access policies
 *
 * Provides data fetching and mutations for tenant conditional access policies
 * with automatic caching and cache invalidation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConditionalAccessApiService } from '../services/conditionalAccessApi';
import type {
  ConditionalAccessPolicyResponse,
  ConditionalAccessPolicyCreateRequest,
} from '../types/conditionalAccess';

/**
 * Query key factory for conditional access policies
 */
export const conditionalAccessPoliciesQueryKey = (tenantId: string) =>
  ['tenant', tenantId, 'conditionalAccessPolicies'] as const;

/**
 * Hook return type for useConditionalAccessPolicies
 */
export interface UseConditionalAccessPoliciesResult {
  /** Conditional access policies data */
  policies: ConditionalAccessPolicyResponse[] | undefined;
  /** Loading state for initial fetch */
  isLoading: boolean;
  /** Fetching state (true during refetch) */
  isFetching: boolean;
  /** Error from fetching policies */
  error: Error | null;
  /** Refetch policies */
  refetch: () => void;
  /** Function to create a policy */
  createPolicy: (request: ConditionalAccessPolicyCreateRequest) => Promise<ConditionalAccessPolicyResponse>;
  /** Loading state for create mutation */
  isCreating: boolean;
  /** Error from create mutation */
  createError: Error | null;
  /** Function to delete a policy */
  deletePolicy: (policyId: string) => Promise<{ message: string }>;
  /** Loading state for delete mutation */
  isDeleting: boolean;
  /** Error from delete mutation */
  deleteError: Error | null;
  /** Function to delete multiple policies */
  bulkDeletePolicies: (policyIds: string[]) => Promise<void>;
  /** Loading state for bulk delete mutation */
  isBulkDeleting: boolean;
  /** Error from bulk delete mutation */
  bulkDeleteError: Error | null;
}

/**
 * React Query hook for managing conditional access policies
 *
 * @param tenantId - Tenant ID to fetch conditional access policies for
 * @returns Policies data, loading states, and mutation functions
 */
export function useConditionalAccessPolicies(
  tenantId: string
): UseConditionalAccessPoliciesResult {
  const queryClient = useQueryClient();

  // Query for fetching all conditional access policies
  const {
    data: policies,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: conditionalAccessPoliciesQueryKey(tenantId),
    queryFn: () => ConditionalAccessApiService.listConditionalAccessPolicies(tenantId),
  });

  // Mutation for creating a policy
  const {
    mutateAsync: createPolicy,
    isPending: isCreating,
    error: createError,
  } = useMutation({
    mutationFn: (request: ConditionalAccessPolicyCreateRequest) =>
      ConditionalAccessApiService.createConditionalAccessPolicy(tenantId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conditionalAccessPoliciesQueryKey(tenantId),
      });
    },
  });

  // Mutation for deleting a single policy
  const {
    mutateAsync: deletePolicy,
    isPending: isDeleting,
    error: deleteError,
  } = useMutation({
    mutationFn: (policyId: string) =>
      ConditionalAccessApiService.deleteConditionalAccessPolicy(tenantId, policyId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conditionalAccessPoliciesQueryKey(tenantId),
      });
    },
  });

  // Mutation for bulk deleting policies
  const {
    mutateAsync: bulkDeletePolicies,
    isPending: isBulkDeleting,
    error: bulkDeleteError,
  } = useMutation({
    mutationFn: async (policyIds: string[]) => {
      // Delete policies sequentially to avoid rate limiting
      for (const policyId of policyIds) {
        await ConditionalAccessApiService.deleteConditionalAccessPolicy(tenantId, policyId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conditionalAccessPoliciesQueryKey(tenantId),
      });
    },
  });

  return {
    policies,
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch,
    createPolicy,
    isCreating,
    createError: createError as Error | null,
    deletePolicy,
    isDeleting,
    deleteError: deleteError as Error | null,
    bulkDeletePolicies,
    isBulkDeleting,
    bulkDeleteError: bulkDeleteError as Error | null,
  };
}
